import { Alert, Badge, Stack, Table, Text } from '@mantine/core';
import type { LegacyMigrationVerification } from '../../services/legacyUserManager';

export function LegacyMigrationVerificationResult({
  verification,
}: {
  verification: LegacyMigrationVerification;
}) {
  return (
    <Alert
      color={
        verification.status === 'pass' &&
        (verification.vapor_to_json?.ok ?? true)
          ? 'teal'
          : verification.status === 'not_linked' &&
              (verification.vapor_to_json?.ok ?? true)
            ? 'blue'
            : 'red'
      }
      title={`Verify: ${verification.status}${
        verification.be_user_id ? ` · be2025 #${verification.be_user_id}` : ''
      }${
        verification.verify_mode
          ? ` · ${verification.verify_mode}`
          : ''
      }`}
    >
      <Stack gap='xs'>
        {verification.vapor_to_json ? (
          <Text size='sm'>
            vapor→json:{' '}
            {verification.vapor_to_json.ok ? (
              <Badge color='teal' size='sm' variant='light'>
                OK
              </Badge>
            ) : (
              <Badge color='red' size='sm' variant='filled'>
                FAIL · {verification.vapor_to_json.summary.mismatches}
              </Badge>
            )}{' '}
            · entitlements{' '}
            {verification.vapor_to_json.counts.vapor.entitlements}/
            {verification.vapor_to_json.counts.json.entitlements} ·
            progress {verification.vapor_to_json.counts.vapor.progress}/
            {verification.vapor_to_json.counts.json.progress} ·
            answers {verification.vapor_to_json.counts.vapor.answers}/
            {verification.vapor_to_json.counts.json.answers} ·
            videos {verification.vapor_to_json.counts.vapor.videos}/
            {verification.vapor_to_json.counts.json.videos}
          </Text>
        ) : (
          <Text size='sm' c='dimmed'>
            vapor→json: not checked (no export body)
          </Text>
        )}
        <Text size='sm'>
          json→be:{' '}
          <Badge
            color={
              verification.status === 'pass'
                ? 'teal'
                : verification.status === 'not_linked'
                  ? 'blue'
                  : 'red'
            }
            size='sm'
            variant='light'
          >
            {verification.status}
          </Badge>
        </Text>
        <Text size='sm'>
          identity{' '}
          {verification.summary.user_info_present
            ? `${verification.summary.user_info_fields_matched}/${verification.summary.user_info_fields_expected}${
                verification.user_info?.source
                  ? ` (${verification.user_info.source})`
                  : ''
              }${
                verification.summary.user_info_fields_kept
                  ? ` · ${verification.summary.user_info_fields_kept} già in be2025`
                  : ''
              }`
            : 'MISSING'}{' '}
          · progress{' '}
          {verification.summary.progress_fields_matched}/
          {verification.summary.progress_fields_expected} · courses{' '}
          {verification.summary.courses_matched}/
          {verification.summary.courses_expected} · products{' '}
          {verification.summary.products_matched}/
          {verification.summary.products_expected} · answers{' '}
          {verification.summary.answers_keys_matched}/
          {verification.summary.answers_keys_expected} · reviews{' '}
          {verification.summary.reviews_matched}/
          {verification.summary.reviews_expected}
          {verification.summary.content_fields_expected != null ? (
            <>
              {' · content '}
              {verification.summary.content_fields_matched}/
              {verification.summary.content_fields_expected}
            </>
          ) : null}
          {verification.summary.out_of_scope_rows ? (
            <>
              {' · '}
              <Text span c='red' fw={700}>
                out-of-scope {verification.summary.out_of_scope_rows}
              </Text>
            </>
          ) : null}
        </Text>
        {verification.inventory ? (
          <>
            <Text size='sm' fw={600}>
              Inventory counts (json plan → be2025)
            </Text>
            <Text size='sm'>
              courses: entitlements{' '}
              {verification.inventory.counts.vapor_entitlements} · linked{' '}
              <Text
                span
                c={
                  verification.inventory.count_ok.vapor_linked_vs_plan_courses
                    ? undefined
                    : 'red'
                }
                fw={
                  verification.inventory.count_ok.vapor_linked_vs_plan_courses
                    ? undefined
                    : 700
                }
              >
                {verification.inventory.counts.vapor_linked_courses}
              </Text>
              {' → plan '}
              <Text
                span
                c={
                  verification.inventory.count_ok.plan_vs_be_courses
                    ? undefined
                    : 'red'
                }
                fw={
                  verification.inventory.count_ok.plan_vs_be_courses
                    ? undefined
                    : 700
                }
              >
                {verification.inventory.counts.plan_courses}
              </Text>
              {' → be '}
              {verification.inventory.counts.be_courses}
              {' · videos: vapor '}
              <Text
                span
                c={
                  verification.inventory.count_ok
                    .vapor_videos_vs_plan_video_products
                    ? undefined
                    : 'red'
                }
              >
                {verification.inventory.counts.vapor_videos}
              </Text>
              {' → plan-video-products '}
              {verification.inventory.counts.plan_products_from_video}
              {' · products: course '}
              {verification.inventory.counts.plan_products_from_course}
              {' + video '}
              {verification.inventory.counts.plan_products_from_video}
              {' = plan '}
              <Text
                span
                c={
                  verification.inventory.count_ok.plan_vs_be_products
                    ? undefined
                    : 'red'
                }
              >
                {verification.inventory.counts.plan_products}
              </Text>
              {' → be '}
              {verification.inventory.counts.be_products}
            </Text>
            {verification.inventory.warnings.length > 0 ? (
              <Stack gap={4}>
                {verification.inventory.warnings.map((w) => (
                  <Alert key={w} color='yellow' p='xs'>
                    <Text size='xs'>{w}</Text>
                  </Alert>
                ))}
              </Stack>
            ) : null}
            <Text size='sm' fw={600}>
              Courses inventory
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>vapor</Table.Th>
                  <Table.Th>name</Table.Th>
                  <Table.Th>sources</Table.Th>
                  <Table.Th>be</Table.Th>
                  <Table.Th>product</Table.Th>
                  <Table.Th>status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {verification.inventory.courses.map((c) => (
                  <Table.Tr key={`${c.vapor_course_id}-${c.status}`}>
                    <Table.Td>{c.vapor_course_id}</Table.Td>
                    <Table.Td>
                      <Text size='xs'>{c.vapor_name ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs'>
                        {[
                          c.sources.entitlement ? 'ent' : null,
                          c.sources.progress ? 'prog' : null,
                          c.sources.progress_old ? 'old' : null,
                          c.sources.answers ? 'ans' : null,
                          c.sources.feedback ? 'fb' : null,
                          c.sources.tests ? 'test' : null,
                        ]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>{c.be_course_id ?? '—'}</Table.Td>
                    <Table.Td>{c.product_id ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge
                        size='sm'
                        color={
                          c.status === 'ok'
                            ? 'teal'
                            : c.status === 'extra_in_be'
                              ? 'blue'
                              : 'red'
                        }
                        variant='light'
                      >
                        {c.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Text size='sm' fw={600}>
              Videos / products inventory
            </Text>
            {verification.inventory.videos.length === 0 ? (
              <Text size='xs' c='dimmed'>
                No vapor videos (user_has_videos empty). Products below
                come from courses.
              </Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>vapor video</Table.Th>
                    <Table.Th>name</Table.Th>
                    <Table.Th>be video</Table.Th>
                    <Table.Th>product</Table.Th>
                    <Table.Th>status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {verification.inventory.videos.map((v) => (
                    <Table.Tr key={v.vapor_video_id}>
                      <Table.Td>{v.vapor_video_id}</Table.Td>
                      <Table.Td>
                        <Text size='xs'>{v.vapor_name ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>{v.be_video_id ?? '—'}</Table.Td>
                      <Table.Td>{v.product_id ?? '—'}</Table.Td>
                      <Table.Td>
                        <Badge
                          size='sm'
                          color={v.status === 'ok' ? 'teal' : 'red'}
                          variant='light'
                        >
                          {v.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>product</Table.Th>
                  <Table.Th>source</Table.Th>
                  <Table.Th>vapor ref</Table.Th>
                  <Table.Th>status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {verification.inventory.products.map((p) => (
                  <Table.Tr key={`${p.product_id}-${p.source}`}>
                    <Table.Td>{p.product_id}</Table.Td>
                    <Table.Td>{p.source}</Table.Td>
                    <Table.Td>{p.vapor_ref_id ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge
                        size='sm'
                        color={
                          p.status === 'ok'
                            ? 'teal'
                            : p.status === 'extra_in_be'
                              ? 'blue'
                              : 'red'
                        }
                        variant='light'
                      >
                        {p.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {verification.inventory.scope?.length ? (
              <>
                <Text size='sm' fw={600}>
                  Migration scope (all vapor domains)
                </Text>
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>domain</Table.Th>
                      <Table.Th>vapor table</Table.Th>
                      <Table.Th>rows</Table.Th>
                      <Table.Th>in migrate?</Table.Th>
                      <Table.Th>note</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {verification.inventory.scope.map((s) => (
                      <Table.Tr key={s.domain}>
                        <Table.Td>{s.domain}</Table.Td>
                        <Table.Td>
                          <Text size='xs'>{s.vapor_table}</Text>
                        </Table.Td>
                        <Table.Td>{s.count}</Table.Td>
                        <Table.Td>
                          <Badge
                            size='sm'
                            color={
                              s.in_migration_scope
                                ? 'teal'
                                : s.count > 0
                                  ? 'red'
                                  : 'gray'
                            }
                            variant='light'
                          >
                            {s.in_migration_scope
                              ? 'yes'
                              : s.count > 0
                                ? 'DATA LOSS RISK'
                                : 'not in scope'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size='xs'>{s.note}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : null}
            {verification.inventory.content?.length ? (
              <>
                <Text size='sm' fw={600}>
                  Content field checks (profile / courses / products)
                </Text>
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>domain</Table.Th>
                      <Table.Th>field</Table.Th>
                      <Table.Th>expected</Table.Th>
                      <Table.Th>actual</Table.Th>
                      <Table.Th>status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {verification.inventory.content
                      .slice(0, 120)
                      .map((c, idx) => (
                        <Table.Tr key={`${c.path}-${idx}`}>
                          <Table.Td>{c.domain}</Table.Td>
                          <Table.Td>
                            <Text size='xs' style={{ wordBreak: 'break-all' }}>
                              {c.path}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size='xs' style={{ wordBreak: 'break-all' }}>
                              {typeof c.expected === 'object'
                                ? JSON.stringify(c.expected)
                                : String(c.expected)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size='xs' style={{ wordBreak: 'break-all' }}>
                              {c.actual == null
                                ? '—'
                                : typeof c.actual === 'object'
                                  ? JSON.stringify(c.actual)
                                  : String(c.actual)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size='sm'
                              color={
                                !c.ok
                                  ? 'red'
                                  : c.status === 'kept_or_ahead'
                                    ? 'blue'
                                    : 'teal'
                              }
                              variant='light'
                            >
                              {c.status}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                  </Table.Tbody>
                </Table>
                {verification.inventory.content.length > 120 ? (
                  <Text size='sm' c='dimmed'>
                    …and {verification.inventory.content.length - 120} more
                    content checks
                  </Text>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
        {verification.user_info?.fields?.length ? (
          <>
            <Text size='sm' fw={600}>
              {verification.user_info.source === 'professionals'
                ? 'professionals → Users'
                : 'user_info → Users'}
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>vapor</Table.Th>
                  <Table.Th>be2025</Table.Th>
                  <Table.Th>expected</Table.Th>
                  <Table.Th>actual</Table.Th>
                  <Table.Th>ok</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {verification.user_info.fields.map((f) => (
                  <Table.Tr key={`${f.vapor_field}-${f.be_field}`}>
                    <Table.Td>{f.vapor_field}</Table.Td>
                    <Table.Td>{f.be_field}</Table.Td>
                    <Table.Td>
                      <Text size='xs'>{String(f.expected)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='xs'>
                        {f.actual == null ? '—' : String(f.actual)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size='sm'
                        color={
                          !f.ok
                            ? 'red'
                            : f.status === 'kept_existing'
                              ? 'blue'
                              : 'teal'
                        }
                        variant='light'
                      >
                        {!f.ok
                          ? 'fail'
                          : f.status === 'kept_existing'
                            ? 'già in be2025'
                            : 'ok'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        ) : null}
        {verification.progress?.fields?.length ? (
          <>
            <Text size='sm' fw={600}>
              progress → UserCourses (section / time / flags)
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>vapor course</Table.Th>
                  <Table.Th>be course</Table.Th>
                  <Table.Th>field</Table.Th>
                  <Table.Th>expected</Table.Th>
                  <Table.Th>actual</Table.Th>
                  <Table.Th>ok</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {verification.progress.fields
                  .slice(0, 80)
                  .map((f, idx) => (
                    <Table.Tr
                      key={`${f.vapor_course_id}-${f.field}-${idx}`}
                    >
                      <Table.Td>{f.vapor_course_id}</Table.Td>
                      <Table.Td>{f.be_course_id}</Table.Td>
                      <Table.Td>{f.field}</Table.Td>
                      <Table.Td>
                        <Text size='xs' style={{ wordBreak: 'break-all' }}>
                          {typeof f.expected === 'object'
                            ? JSON.stringify(f.expected)
                            : String(f.expected)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='xs' style={{ wordBreak: 'break-all' }}>
                          {f.actual == null
                            ? '—'
                            : typeof f.actual === 'object'
                              ? JSON.stringify(f.actual)
                              : String(f.actual)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size='sm'
                          color={f.ok ? 'teal' : 'red'}
                          variant='light'
                        >
                          {f.ok ? 'ok' : 'fail'}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
            {verification.progress.fields.length > 80 ? (
              <Text size='sm' c='dimmed'>
                …and {verification.progress.fields.length - 80} more
                progress checks
              </Text>
            ) : null}
          </>
        ) : null}
        {verification.plan_error ? (
          <Text size='sm'>{verification.plan_error}</Text>
        ) : null}
        {verification.mismatches.length > 0 ? (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>path</Table.Th>
                <Table.Th>reason</Table.Th>
                <Table.Th>expected</Table.Th>
                <Table.Th>actual</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {verification.mismatches.slice(0, 40).map((m, idx) => (
                <Table.Tr key={`${m.path}-${idx}`}>
                  <Table.Td>
                    <Text size='xs' style={{ wordBreak: 'break-all' }}>
                      {m.path}
                    </Text>
                  </Table.Td>
                  <Table.Td>{m.reason}</Table.Td>
                  <Table.Td>
                    <Text size='xs' style={{ wordBreak: 'break-all' }}>
                      {typeof m.expected === 'object'
                        ? JSON.stringify(m.expected)
                        : String(m.expected)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='xs' style={{ wordBreak: 'break-all' }}>
                      {typeof m.actual === 'object'
                        ? JSON.stringify(m.actual)
                        : String(m.actual)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
        {verification.mismatches.length > 40 ? (
          <Text size='sm' c='dimmed'>
            …and {verification.mismatches.length - 40} more
          </Text>
        ) : null}
      </Stack>
    </Alert>
  );
}
